import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Settings,
  ChevronRight,
  ChevronLeft,
  Calendar,
  FileText,
  Edit,
  QrCode,
} from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { formatDate } from "../../utils";
import { DropdownOption } from "../../types/dropdown";
import Dropdown from "../../components/dropdown/Index";
import useMallTenantDropdown from "../../hooks/useMallTenantDropdown";
import { fetchDropdownData } from "../../utils";
import { GetWorkPermit, WorkPermitHeaderListData } from "../../services/workPermitService";
import QRCodeModal from "../../components/workPermit/QRCodeModal";
import { getWorkPermitStatusColor } from "../../utils";

interface HeaderDisplayItem extends WorkPermitHeaderListData {
  WorkPermitId?: number;
  PermitTypeId?: number;
  PermitTypeName: string;
  PermitStatusDesc: string;
  PermitStatus?: number;
  TenantName: string;
  VendorName: string;
}

const ITEMS_PER_PAGE = 7;

const DEFAULT_PERMIT_STATUS_OPTIONS: DropdownOption[] = [
  { value: 1, label: "Draft" },
  { value: 10, label: "Pending Approval" },
  { value: 20, label: "Approved" },
  { value: 30, label: "Reject" },
];

const DEFAULT_PERMIT_TYPE_OPTIONS: DropdownOption[] = [
  { value: 30, label: "Surat Izin Kerja" },
  { value: 10, label: "Surat Izin Masuk" },
  { value: 20, label: "Surat Izin Keluar" },
];

const fromYMD = (s: string) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const isDateInRange = (dateStr: string, startRange: string, endRange: string) => {
  const date = new Date(dateStr);
  const start = fromYMD(startRange);
  const end = fromYMD(endRange);
  date.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return date >= start && date <= end;
};

interface CalendarPickerProps {
  startDate: string;
  endDate: string;
  onSelectRange: (start: string, end: string) => void;
}

const CalendarPicker: React.FC<CalendarPickerProps> = ({ startDate, endDate, onSelectRange }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const daysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const formatYMD = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const parseYMD = (ymd: string) => {
    if (!ymd) return null;
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const clickedYMD = formatYMD(clickedDate);
    if (!startDate || (startDate && endDate)) {
      onSelectRange(clickedYMD, "");
    } else if (startDate && !endDate) {
      const start = parseYMD(startDate);
      if (clickedDate >= start!) {
        onSelectRange(startDate, clickedYMD);
      } else {
        onSelectRange(clickedYMD, startDate);
      }
    }
  };

  const isInRange = (day: number) => {
    if (!startDate || !endDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateYMD = formatYMD(date);
    return dateYMD >= startDate && dateYMD <= endDate;
  };

  const isStartOrEnd = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dateYMD = formatYMD(date);
    return dateYMD === startDate || dateYMD === endDate;
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const renderDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-9" />);
    }
    for (let day = 1; day <= totalDays; day++) {
      const inRange = isInRange(day);
      const isStartEnd = isStartOrEnd(day);
      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-9 w-9 flex items-center justify-center text-sm rounded-lg transition-all ${isStartEnd
            ? "bg-blue-600 text-white font-semibold shadow-md"
            : inRange
              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
              : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            }`}
        >
          {day}
        </button>
      );
    }
    return days;
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
          <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div className="text-base font-semibold text-gray-900 dark:text-white">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </div>
        <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition">
          <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-2">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
          <div key={day} className="h-9 flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">{renderDays()}</div>
    </div>
  );
};

const WorkPermit = () => {
  const [permitTypeFilter, setPermitTypeFilter] = useState<string>("All");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [permitTypeOptions, setPermitTypeOptions] = useState<DropdownOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<DropdownOption[]>([]);
  const [workPermitData, setWorkPermitData] = useState<HeaderDisplayItem[]>([]);
  const [q] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedQRPermitId, setSelectedQRPermitId] = useState<number>(0);
  const [dropdownPosition,] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();
  const location = useLocation();

  const isVendorContext = location.pathname.includes("vendor-management");
  const formBasePath = isVendorContext ? "/vendor-management/work-permit/form" : "/workpermit/form";

  const fetchWorkPermits = async (
    workpermitid: number, permittypeid: number, permitstatus: number,
    mallid: number, tenantid: number, page: number, number: number
  ) => {
    try {
      const response = await GetWorkPermit(workpermitid, permittypeid, permitstatus, mallid, tenantid, page, number);

      const getPermitTypeNameIndo = (permitTypeId?: number, rawName?: string): string => {
        const id = Number(permitTypeId);
        if (id === 30) return "Surat Izin Kerja";
        if (id === 10) return "Surat Izin Masuk";
        if (id === 20) return "Surat Izin Keluar";

        if (rawName) {
          const lower = rawName.toLowerCase();
          if (lower.includes("work") || lower.includes("kerja")) return "Surat Izin Kerja";
          if (lower.includes("in") || lower.includes("masuk")) return "Surat Izin Masuk";
          if (lower.includes("out") || lower.includes("keluar")) return "Surat Izin Keluar";
          return rawName;
        }
        return "-";
      };

      const allPermits: HeaderDisplayItem[] = [];
      if (response.workPermitHeaderDatas) {
        response.workPermitHeaderDatas.forEach((header) => {
          if (header.workPermitDatas && header.workPermitDatas.length > 0) {
            header.workPermitDatas.forEach((wp) => {
              const typeName = getPermitTypeNameIndo(wp.PermitTypeId, wp.PermitTypeName);

              let permitStatusDesc = wp.PermitStatusDesc || header.PermitHeaderStatusDesc || "";
              if (!permitStatusDesc) {
                const statusVal = wp.PermitStatus !== undefined ? wp.PermitStatus : header.PermitHeaderStatus;
                if (statusVal === 10 || statusVal === 40) permitStatusDesc = "Pending Approval";
                else if (statusVal === 20) permitStatusDesc = "Approved";
                else if (statusVal === 30) permitStatusDesc = "Reject";
                else permitStatusDesc = "Draft";
              }

              allPermits.push({
                ...header,
                WorkPermitId: wp.WorkPermitId,
                PermitTypeId: wp.PermitTypeId,
                WorkPermitHeaderId: header.WorkPermitHeaderId,
                PermitNumber: wp.PermitNumber || header.PermitNumberHeader || header.PermitNumber,
                PermitTypeName: typeName,
                PermitStatusDesc: permitStatusDesc,
                PermitStatus: wp.PermitStatus !== undefined ? wp.PermitStatus : header.PermitHeaderStatus,
                TenantName: wp.TenantName || header.TenantName || "",
                VendorName: wp.VendorName || header.VendorName || "",
                StartOn: wp.StartOn || header.StartOn,
                FinishOn: wp.FinishOn || header.FinishOn,
                StartTime: wp.StartTime || header.StartTime,
                FinishTime: wp.FinishTime || header.FinishTime,
                personnelDatas: (wp.personnelDatas && wp.personnelDatas.length > 0) ? wp.personnelDatas : (header.personnelDatas || []),
                itemDatas: (wp.itemDatas && wp.itemDatas.length > 0) ? wp.itemDatas : (header.itemDatas || []),
                workPermitDatas: header.workPermitDatas || [],
              });
            });
          } else {
            let permitTypeName = "-";
            if (header.PermitTypeIds && header.PermitTypeIds.length > 0) {
              const names = header.PermitTypeIds.map((id) => getPermitTypeNameIndo(id));
              permitTypeName = names.join(", ");
            }

            let permitStatusDesc = header.PermitHeaderStatusDesc || "";
            if (!permitStatusDesc) {
              const statusVal = header.PermitHeaderStatus;
              if (statusVal === 10 || statusVal === 40) permitStatusDesc = "Pending Approval";
              else if (statusVal === 20) permitStatusDesc = "Approved";
              else if (statusVal === 30) permitStatusDesc = "Reject";
              else permitStatusDesc = "Draft";
            }

            allPermits.push({
              ...header,
              PermitNumber: header.PermitNumberHeader || header.PermitNumber,
              PermitTypeName: permitTypeName,
              PermitStatusDesc: permitStatusDesc,
              PermitStatus: header.PermitHeaderStatus,
              TenantName: header.TenantName || "",
              VendorName: header.VendorName || "",
              personnelDatas: header.personnelDatas || [],
              itemDatas: header.itemDatas || [],
              workPermitDatas: header.workPermitDatas || [],
            });
          }
        });
      }

      const sortedData = [...allPermits].sort((a, b) => {
        const idA = a.WorkPermitId || a.WorkPermitHeaderId;
        const idB = b.WorkPermitId || b.WorkPermitHeaderId;
        return idB - idA;
      });
      setWorkPermitData(sortedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const { mallOptions, tenantOptions, selectedMall, selectedTenant, setSelectedMall, setSelectedTenant } =
    useMallTenantDropdown((tenantId, mallId) => {
      fetchWorkPermits(0, 0, 0, mallId, tenantId, 0, 0);
    });

  useEffect(() => {
    fetchDropdownData("permitTypeOptions", 0)
      .then((data) => setPermitTypeOptions(data || []))
      .catch((error) => console.error("Failed to fetch permit type options:", error));

    fetchDropdownData("permitStatusOptions", 0)
      .then((data) => setStatusOptions(data || []))
      .catch((error) => console.error("Failed to fetch permit status options:", error));
  }, []);

  const effectiveStatusOptions = useMemo(() => {
    const map = new Map<string, DropdownOption>();
    DEFAULT_PERMIT_STATUS_OPTIONS.forEach((opt) => map.set(opt.label.toLowerCase(), opt));
    statusOptions.forEach((opt) => {
      if (opt && opt.label) map.set(opt.label.toLowerCase(), opt);
    });
    workPermitData.forEach((item) => {
      if (item.PermitStatusDesc && !map.has(item.PermitStatusDesc.toLowerCase())) {
        map.set(item.PermitStatusDesc.toLowerCase(), {
          value: map.size + 100,
          label: item.PermitStatusDesc,
        });
      }
    });
    return Array.from(map.values()).filter((opt) => opt.label.toLowerCase() !== "waiting");
  }, [statusOptions, workPermitData]);

  const effectivePermitTypeOptions = useMemo(() => {
    const map = new Map<string, DropdownOption>();
    DEFAULT_PERMIT_TYPE_OPTIONS.forEach((opt) => map.set(opt.label.toLowerCase(), opt));
    permitTypeOptions.forEach((opt) => {
      if (opt && opt.label) map.set(opt.label.toLowerCase(), opt);
    });
    return Array.from(map.values());
  }, [permitTypeOptions]);

  const handleEdit = (id: number, permitTypeId?: number) => {
    let targetType = 1;
    if (permitTypeId === 10) targetType = 2;
    else if (permitTypeId === 20) targetType = 3;
    else if (permitTypeId === 30) targetType = 1;

    navigate(`${formBasePath}/${id}`, {
      state: { MallId: selectedMall, TenantOptions: tenantOptions, targetPermitType: targetType },
    });
  };

  const handleShowQRCode = (id: number, permitTypeId?: number) => {
    let targetType = 1;
    if (permitTypeId === 10) targetType = 2;
    else if (permitTypeId === 20) targetType = 3;
    else if (permitTypeId === 30) targetType = 1;

    navigate(`${formBasePath}/${id}`, {
      state: { MallId: selectedMall, TenantOptions: tenantOptions, viewMode: true, targetPermitType: targetType },
    });
  };
  const handleOpenQRModal = (id: number) => {
    setSelectedQRPermitId(id);
    setShowQRModal(true);
  };

  const isPermitApproved = (permit: HeaderDisplayItem): boolean => {
    if (permit.PermitStatus === 20) return true;
    if (permit.workPermitDatas && permit.workPermitDatas.length > 0) {
      return permit.workPermitDatas.every((wp) => wp.PermitStatus === 20);
    }
    return (
      permit.PermitHeaderStatus === 20 ||
      permit.PermitStatusDesc?.toLowerCase() === "approved" ||
      permit.PermitStatusDesc?.toLowerCase() === "request approved"
    );
  };

  const handleCloseQRModal = () => { setShowQRModal(false); setSelectedQRPermitId(0); };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filtered.map(({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      personnelDatas: _p,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      itemDatas: _i,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      workPermitDatas: _w,
      ...rest
    }) => rest)), "WorkPermit");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      filtered.flatMap((wp) => (wp.personnelDatas || []).map((p) => ({ WorkPermitHeaderId: wp.WorkPermitHeaderId, PermitNumber: wp.PermitNumber, PersonnelName: p.PersonnelName, IdentityNumber: p.IdentityNumber })))
    ), "Personnel");
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      filtered.flatMap((wp) => (wp.itemDatas || []).map((i) => ({ WorkPermitHeaderId: wp.WorkPermitHeaderId, PermitNumber: wp.PermitNumber, Item: i.Description, Qty: i.ItemQuantity, Unit: i.ItemUnit })))
    ), "Items");
    XLSX.writeFile(wb, "WorkPermit-Data-Export.xlsx");
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Exporting to Excel...", showConfirmButton: false, timer: 2000 });
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text("Work Permit Report", 14, 15);
    doc.setFontSize(10); doc.text(`Total Record: ${filtered.length}`, 14, 22);
    doc.text(`Generated At: ${new Date().toLocaleString("id-ID")}`, 14, 28);
    autoTable(doc, { startY: 35, head: [["Permit No", "Type", "Tenant", "Vendor", "Status", "Start Date"]], body: filtered.map((wp) => [wp.PermitNumber || "", wp.PermitTypeName, wp.TenantName, wp.VendorName ?? "", wp.PermitStatusDesc, formatDate(wp.StartOn)]) });
    filtered.forEach((wp, index) => {
      doc.addPage();
      doc.setFontSize(12); doc.text(`Permit Detail #${index + 1}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Permit Number : ${wp.PermitNumber || ""}`, 14, 25);
      doc.text(`Tenant        : ${wp.TenantName}`, 14, 32);
      doc.text(`Vendor        : ${wp.VendorName}`, 14, 39);
      doc.text(`Permit Type   : ${wp.PermitTypeName}`, 14, 46);
      doc.text(`Schedule      : ${formatDate(wp.StartOn)} ${wp.StartTime} - ${wp.FinishTime}`, 14, 53);
      doc.setTextColor(wp.PermitStatusDesc === "Request Approved" || wp.PermitStatusDesc === "Approved" ? 0 : 150, wp.PermitStatusDesc === "Request Approved" || wp.PermitStatusDesc === "Approved" ? 128 : 0, 0);
      doc.text(`Status        : ${wp.PermitStatusDesc}`, 14, 60);
      doc.setTextColor(0, 0, 0);
      if (wp.personnelDatas?.length) {
        doc.setFontSize(11); doc.text("Personnel", 14, 68);
        autoTable(doc, { startY: 70, head: [["Name", "Identity Number", "Access Card", "Parking Key"]], body: wp.personnelDatas.map((p) => [p.PersonnelName, p.IdentityNumber, p.IsAccessCard ? "Yes" : "No", p.IsParkingLotKey ? "Yes" : "No"]) });
      }
      if (wp.itemDatas?.length) {
        const y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable ? (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6 : 68;
        doc.setFontSize(11); doc.text("Items", 14, y);
        autoTable(doc, { startY: y + 4, head: [["Item", "Qty", "Unit"]], body: wp.itemDatas.map((i) => [i.Description, i.ItemQuantity, i.ItemUnit]) });
      }
    });
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i); doc.setFontSize(9);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() - 30, doc.internal.pageSize.getHeight() - 10);
    }
    doc.save(`work-permit-report-${Date.now()}.pdf`);
    Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Exporting to PDF...", showConfirmButton: false, timer: 2000 });
  };

  const handleCreate = () => {
    navigate(`${formBasePath}/new`, { state: { MallId: selectedMall, TenantOptions: tenantOptions } });
  };

  const handleSelectRange = (start: string, end: string) => { setStartDate(start); setEndDate(end); };
  const handleClearDateRange = () => { setStartDate(""); setEndDate(""); };

  const filtered = useMemo(() => {
    let base = workPermitData;
    if (permitTypeFilter !== "All") {
      const filterLower = permitTypeFilter.toLowerCase();
      base = base.filter((p) => {
        const typeLower = p.PermitTypeName.toLowerCase();
        return (
          typeLower.includes(filterLower) ||
          filterLower.includes(typeLower) ||
          (filterLower.includes("work") && typeLower.includes("kerja")) ||
          (filterLower.includes("in") && typeLower.includes("masuk")) ||
          (filterLower.includes("out") && typeLower.includes("keluar"))
        );
      });
    }
    if (statusFilter !== "All") base = base.filter((p) => p.PermitStatusDesc === statusFilter);
    if (startDate && endDate) base = base.filter((p) => isDateInRange(p.StartOn, startDate, endDate));
    const qq = q.trim().toLowerCase();
    if (!qq) return base;
    return base.filter((p) =>
      (p.PermitNumber || "").toLowerCase().includes(qq) ||
      p.PermitTypeName.toLowerCase().includes(qq) ||
      p.PermitStatusDesc.toLowerCase().includes(qq)
    );
  }, [q, permitTypeFilter, statusFilter, workPermitData, startDate, endDate]);

  const rawTotalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const totalPages = Math.max(1, rawTotalPages);

  useEffect(() => { setCurrentPage(1); }, [q, permitTypeFilter, statusFilter, startDate, endDate]);
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

  useEffect(() => {
    const handleScroll = () => { if (showActionMenu) setShowActionMenu(null); };
    const scrollContainer = document.querySelector(".overflow-x-auto");
    if (scrollContainer) scrollContainer.addEventListener("scroll", handleScroll);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      if (scrollContainer) scrollContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [showActionMenu]);

  const paginatedPermits = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const renderPageButtons = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push("...");
    pages.push(totalPages);
    return pages;
  };

  return (
    <>
      <Dropdown
        mallSelected={setSelectedMall}
        mallOptions={mallOptions}
        tenantSelected={setSelectedTenant}
        tenantOptions={tenantOptions}
        mallValue={selectedMall}
        tenantValue={selectedTenant}
      />

      <div className="min-h-screen bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 space-y-4 sm:space-y-6">


          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Jenis Permit</label>
              <div className="relative">
                <Settings className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  value={permitTypeFilter}
                  onChange={(e) => setPermitTypeFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All</option>
                  {effectivePermitTypeOptions.map((opt) => <option key={`${opt.value}-${opt.label}`} value={opt.label}>{opt.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tanggal</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 z-10 pointer-events-none" />
                <input
                  type="text"
                  value={startDate && endDate ? `${startDate} - ${endDate}` : ""}
                  placeholder="Pilih Tanggal"
                  onClick={() => setShowCalendar(!showCalendar)}
                  readOnly
                  className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm cursor-pointer placeholder:text-gray-400"
                />
                {startDate && endDate && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleClearDateRange(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
                {showCalendar && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowCalendar(false)} />
                    <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-20 overflow-hidden">
                      <CalendarPicker startDate={startDate} endDate={endDate} onSelectRange={handleSelectRange} />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
              <div className="relative">
                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="All">All</option>
                  {effectiveStatusOptions.map((opt) => <option key={`${opt.value}-${opt.label}`} value={opt.label}>{opt.label}</option>)}
                </select>
              </div>
            </div>

            <div className="flex items-end">
              <button
                onClick={handleCreate}
                className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition text-sm shadow-sm"
              >
                + Create
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={handleExportExcel} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition shadow-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M12.9,14.5L15.8,19H14L12,15.6L10,19H8.2L11.1,14.5L8.2,10H10L12,13.4L14,10H15.8L12.9,14.5Z" /></svg>
              Excel
            </button>
            <button onClick={handleExportPDF} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition shadow-sm">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10.92,12.31C10.68,11.54 10.15,9.08 11.55,9.04C12.95,9 12.03,12.16 12.03,12.16C12.42,13.65 14.05,14.72 14.05,14.72C14.55,14.57 17.4,14.24 17,15.72C16.57,17.2 13.5,15.81 13.5,15.81C11.55,15.95 10.09,16.47 10.09,16.47C8.96,18.58 7.64,19.5 7.1,18.61C6.43,17.5 9.23,16.07 9.23,16.07C10.68,13.72 10.92,12.31 10.92,12.31Z" /></svg>
              PDF
            </button>
          </div>


          <div className="hidden md:block rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nomor Surat Izin</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Jenis Permit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tanggal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedPermits.map((permit, index) => {
                    const approved = isPermitApproved(permit);
                    const isDraft = permit.PermitStatus === 10 || permit.PermitHeaderStatus === 10 || permit.PermitStatusDesc?.toLowerCase() === "draft";
                    return (
                    <tr
                      key={`${permit.WorkPermitHeaderId}-${permit.WorkPermitId || index}`}
                      onClick={() => {
                        if (isDraft) {
                          handleEdit(permit.WorkPermitHeaderId, permit.PermitTypeId);
                        } else {
                          handleShowQRCode(permit.WorkPermitHeaderId, permit.PermitTypeId);
                        }
                      }}
                      className="hover:bg-slate-50/80 dark:hover:bg-gray-700/50 transition cursor-pointer"
                    >
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-300 text-center">
                        {(currentPage - 1) * ITEMS_PER_PAGE + paginatedPermits.indexOf(permit) + 1}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">{permit.PermitNumber || "-"}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{permit.PermitTypeName}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{formatDate(permit.StartOn)}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getWorkPermitStatusColor(permit.PermitStatusDesc as any)}`}>
                          {permit.PermitStatusDesc}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDraft) {
                              handleEdit(permit.WorkPermitHeaderId, permit.PermitTypeId);
                            } else {
                              handleShowQRCode(permit.WorkPermitHeaderId, permit.PermitTypeId);
                            }
                          }}
                          className="px-3 py-1 text-xs rounded-lg bg-blue-50 border border-blue-200/80 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800/40 text-blue-700 dark:text-blue-300 transition font-medium"
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
              {paginatedPermits.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Tidak ada data yang cocok dengan filter / pencarian.
                </div>
              )}
            </div>
          </div>


          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            {paginatedPermits.length === 0 ? (
              <div className="py-12 px-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Tidak ada data yang cocok dengan filter / pencarian.
              </div>
            ) : (
              paginatedPermits.map((permit, idx) => {
                const approved = isPermitApproved(permit);
                const isDraft = permit.PermitStatus === 10 || permit.PermitHeaderStatus === 10 || permit.PermitStatusDesc?.toLowerCase() === "draft";
                return (
                <div
                  key={`${permit.WorkPermitHeaderId}-${permit.WorkPermitId || idx}`}
                  onClick={() => {
                    if (isDraft) {
                      handleEdit(permit.WorkPermitHeaderId, permit.PermitTypeId);
                    } else {
                      handleShowQRCode(permit.WorkPermitHeaderId, permit.PermitTypeId);
                    }
                  }}
                  className="p-4 space-y-3 cursor-pointer transition bg-white dark:bg-gray-800 hover:bg-slate-50 dark:hover:bg-gray-700/50"
                >

                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 text-xs font-semibold flex items-center justify-center">
                        {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                      </span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {permit.PermitNumber || "-"}
                      </span>
                    </div>
                    <span className={`flex-shrink-0 inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getWorkPermitStatusColor(permit.PermitStatusDesc as any)}`}>
                      {permit.PermitStatusDesc}
                    </span>
                  </div>


                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Jenis Permit</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{permit.PermitTypeName || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">Tanggal</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(permit.StartOn)}</p>
                    </div>
                  </div>


                  <div onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDraft) {
                          handleEdit(permit.WorkPermitHeaderId, permit.PermitTypeId);
                        } else {
                          handleShowQRCode(permit.WorkPermitHeaderId, permit.PermitTypeId);
                        }
                      }}
                      className="w-full py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 border border-blue-200/80 dark:bg-blue-900/20 hover:bg-blue-100 rounded-lg transition text-center"
                    >
                      Open
                    </button>
                  </div>
                </div>
              );
              })
            )}
          </div>


          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Show result:</span>
              <select
                value={ITEMS_PER_PAGE}
                className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value={7}>7</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>


              <span className="sm:hidden text-sm text-gray-600 dark:text-gray-400 px-3">
                {currentPage} / {totalPages}
              </span>


              <div className="hidden sm:flex items-center gap-1.5">
                {renderPageButtons().map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`min-w-[40px] h-10 px-3 rounded-lg font-medium transition text-sm ${currentPage === p
                        ? "bg-blue-600 text-white"
                        : "border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700"
                        }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={`dot-${idx}`} className="text-gray-400 px-1 select-none">...</span>
                  )
                )}
              </div>

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

        </div>
      </div>

      <QRCodeModal isOpen={showQRModal} onClose={handleCloseQRModal} permitId={selectedQRPermitId} />

      {showActionMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowActionMenu(null)} />
          <div
            className="fixed w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
            style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }}
          >
            <button
              onClick={(ev) => {
                ev.stopPropagation();
                handleEdit(showActionMenu);
                setShowActionMenu(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition rounded-t-lg"
            >
              <Edit className="w-4 h-4" /> Edit
            </button>
            <button
              onClick={(ev) => {
                ev.stopPropagation();
                handleShowQRCode(showActionMenu);
                setShowActionMenu(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition rounded-b-lg"
            >
              <QrCode className="w-4 h-4" /> Detail
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default WorkPermit;